import { useEffect, useState } from 'react';
import { ArrowDown, ArrowLeft, ArrowUp, GitBranch, Pencil, Plus, Trash2 } from 'lucide-react';
import { useAiAgents } from '@/api/ai-agents';
import { ApiError } from '@/api/client';
import { useTeams } from '@/api/teams';
import { useAllUsers } from '@/api/users';
import { useWidgets } from '@/api/widgets';
import {
  useAddWorkflowRule,
  useDeleteWorkflow,
  useMoveWorkflowRule,
  useRemoveWorkflowRule,
  useSaveWorkflow,
  useUpdateWorkflowRule,
  useWorkflows,
} from '@/api/workflows';
import type {
  AddRuleInput,
  Team,
  UpdateRuleInput,
  User,
  Widget,
  Workflow,
  WorkflowAction,
  WorkflowRule,
} from '@/api/types';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  ErrorState,
  Input,
  Modal,
  PageHeader,
  Select,
  Skeleton,
  useToast,
} from '@/components/ui';
import { cn } from '@/lib/cn';

type View = { mode: 'list' } | { mode: 'edit'; id: string };

export function WorkflowsPage() {
  const workflows = useWorkflows();
  const [view, setView] = useState<View>({ mode: 'list' });
  const list = workflows.data ?? [];
  const selected = view.mode === 'edit' ? (list.find((w) => w.id === view.id) ?? null) : null;

  useEffect(() => {
    if (view.mode === 'edit' && !workflows.isPending && workflows.data && !selected) {
      setView({ mode: 'list' });
    }
  }, [view, workflows.isPending, workflows.data, selected]);

  if (view.mode === 'edit' && selected) {
    return <WorkflowEditor key={selected.id} workflow={selected} onBack={() => setView({ mode: 'list' })} />;
  }

  return (
    <>
      <PageHeader
        title="Workflow engine"
        description="Route incoming chat conversations by rule: when a message matches — by widget, category or keyword — send it straight to an AI agent, a team queue, or a specific teammate. The first matching rule wins."
        actions={<NewWorkflowButton onCreated={(id) => setView({ mode: 'edit', id })} />}
      />

      {workflows.isPending ? (
        <Skeleton className="h-96 rounded-2xl" />
      ) : workflows.error ? (
        <Card>
          <ErrorState error={workflows.error} onRetry={() => void workflows.refetch()} />
        </Card>
      ) : list.length === 0 ? (
        <Card>
          <EmptyState
            icon={<GitBranch className="h-6 w-6" />}
            title="No workflow yet"
            description="Create a workflow and add rules — for example: keywords “payout, refund” → your payouts AI agent."
            action={<NewWorkflowButton onCreated={(id) => setView({ mode: 'edit', id })} />}
          />
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {list.map((w) => (
            <button
              key={w.id}
              type="button"
              onClick={() => setView({ mode: 'edit', id: w.id })}
              className="group w-full text-left"
            >
              <Card className="hover:border-brand-200 h-full p-5 transition-all hover:-translate-y-0.5 hover:shadow-lg">
                <div className="flex items-start gap-3">
                  <span className="from-brand-500 to-brand-700 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-sm">
                    <GitBranch className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-slate-900">{w.name}</p>
                    <p className="mt-0.5 line-clamp-2 text-sm text-slate-500">
                      {w.description || 'No description yet'}
                    </p>
                  </div>
                  {!w.isEnabled && <Badge>Off</Badge>}
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3 text-xs text-slate-500">
                  <Badge tone={w.rules.length ? 'brand' : undefined}>
                    {w.rules.length ? `${w.rules.length} rule${w.rules.length === 1 ? '' : 's'}` : 'No rules'}
                  </Badge>
                  <span>Rules run top to bottom</span>
                </div>
              </Card>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

function NewWorkflowButton({ onCreated }: { onCreated: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)}>
        <Plus className="h-4 w-4" /> New workflow
      </Button>
      {open && <NewWorkflowModal onClose={() => setOpen(false)} onCreated={onCreated} />}
    </>
  );
}

function NewWorkflowModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const toast = useToast();
  const save = useSaveWorkflow();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const submit = () =>
    save.mutate(
      { input: { name: name.trim(), description: description.trim() } },
      {
        onSuccess: (w) => {
          toast.success('Workflow created — add your first rule');
          onCreated(w.id);
        },
        onError: (e) => {
          const fields = e instanceof ApiError ? e.fieldErrors : {};
          toast.error(Object.values(fields)[0] ?? e.message);
        },
      },
    );

  return (
    <Modal
      open
      onClose={onClose}
      title="New workflow"
      description="A workflow groups the routing rules your chat conversations are checked against."
      size="md"
      footer={
        <div className="ml-auto flex gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" loading={save.isPending} disabled={name.trim().length < 2} onClick={submit}>
            Create workflow
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Input
          label="Name"
          placeholder="Payout routing"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Input
          label="Description"
          placeholder="Optional — what this workflow is for"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
    </Modal>
  );
}

function WorkflowEditor({ workflow, onBack }: { workflow: Workflow; onBack: () => void }) {
  const toast = useToast();
  const save = useSaveWorkflow();
  const remove = useDeleteWorkflow();
  const [name, setName] = useState(workflow.name);
  const [description, setDescription] = useState(workflow.description);
  const [isEnabled, setIsEnabled] = useState(workflow.isEnabled);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editingRule, setEditingRule] = useState<WorkflowRule | 'new' | null>(null);
  useEffect(() => {
    setName(workflow.name);
    setDescription(workflow.description);
    setIsEnabled(workflow.isEnabled);
  }, [workflow]);

  const dirty =
    name !== workflow.name || description !== workflow.description || isEnabled !== workflow.isEnabled;

  const persist = () =>
    save.mutate(
      { id: workflow.id, input: { name: name.trim(), description: description.trim(), isEnabled } },
      {
        onSuccess: () => toast.success('Workflow saved'),
        onError: (e) => {
          const fields = e instanceof ApiError ? e.fieldErrors : {};
          toast.error(Object.values(fields)[0] ?? e.message);
        },
      },
    );

  return (
    <>
      <div className="mb-4 flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" /> All workflows
        </Button>
        {!workflow.isEnabled && <Badge>Off — rules are paused</Badge>}
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="min-w-0 space-y-5">
          <Card className="overflow-hidden">
            <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-4">
              <span className="from-brand-500 to-brand-700 flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-sm">
                <GitBranch className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-slate-900">{name.trim() || 'Workflow'}</p>
                <p className="text-xs text-slate-500">
                  {workflow.rules.length
                    ? `${workflow.rules.length} rule${workflow.rules.length === 1 ? '' : 's'} · checked top to bottom`
                    : 'No rules yet — add one below'}
                </p>
              </div>
              <Checkbox
                label={isEnabled ? 'Live' : 'Paused'}
                checked={isEnabled}
                onChange={(e) => setIsEnabled(e.target.checked)}
              />
            </div>
            <div className="space-y-4 p-5">
              <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
              <Input
                label="Description"
                placeholder="Optional — what this workflow is for"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2 border-t border-slate-100 bg-slate-50/60 px-5 py-3">
              <Button
                variant={confirmDelete ? 'danger' : 'ghost'}
                size="sm"
                loading={remove.isPending}
                onClick={() =>
                  confirmDelete
                    ? remove.mutate(workflow.id, {
                        onSuccess: () => {
                          toast.success('Workflow deleted');
                          onBack();
                        },
                        onError: (e) => toast.error(e.message),
                      })
                    : setConfirmDelete(true)
                }
              >
                {confirmDelete ? 'Confirm delete' : 'Delete workflow'}
              </Button>
              <span className="ml-auto text-xs text-slate-400">
                {dirty ? 'Unsaved changes' : 'All changes saved'}
              </span>
              <Button
                size="sm"
                disabled={!dirty || name.trim().length < 2}
                loading={save.isPending}
                onClick={persist}
              >
                Save workflow
              </Button>
            </div>
          </Card>

          <RulesSection
            workflow={workflow}
            onEdit={(r) => setEditingRule(r)}
            onAdd={() => setEditingRule('new')}
          />
        </div>

        <Card className="h-fit p-5">
          <h3 className="text-sm font-semibold text-slate-900">How routing works</h3>
          <ul className="mt-3 space-y-3 text-sm text-slate-600">
            <li>
              When a new chat or form ticket comes in, rules are checked{' '}
              <span className="font-medium text-slate-800">in order</span> — the first enabled match decides
              who responds.
            </li>
            <li>
              A rule with no conditions matches <span className="font-medium text-slate-800">everything</span>{' '}
              — put it last as a catch-all.
            </li>
            <li>
              Route to an <span className="font-medium text-slate-800">AI agent</span> for instant
              auto-replies, or to a <span className="font-medium text-slate-800">team / teammate</span> queue
              for humans.
            </li>
            <li>
              Nothing matches? The widget&apos;s own AI agent (Settings → Chat widgets) is the fallback.
            </li>
          </ul>
        </Card>
      </div>

      {editingRule && (
        <RuleModal workflow={workflow} rule={editingRule} onClose={() => setEditingRule(null)} />
      )}
    </>
  );
}

function RulesSection({
  workflow,
  onAdd,
  onEdit,
}: {
  workflow: Workflow;
  onAdd: () => void;
  onEdit: (rule: WorkflowRule) => void;
}) {
  const toast = useToast();
  const update = useUpdateWorkflowRule();
  const remove = useRemoveWorkflowRule();
  const move = useMoveWorkflowRule();
  const agents = useAiAgents();
  const teams = useTeams();
  const users = useAllUsers();
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const targetName = (action: WorkflowAction): string => {
    if (action.type === 'agent') {
      const a = (agents.data ?? []).find((x) => x.id === action.id);
      return a ? a.name + (a.isEnabled ? '' : ' (off)') : 'unknown agent';
    }
    if (action.type === 'team') {
      const t = (teams.data ?? []).find((x) => x.id === action.id);
      return t ? t.name : 'unknown team';
    }
    const u = (users.data ?? []).find((x) => x.id === action.id);
    return u ? u.name : 'unknown teammate';
  };

  return (
    <section className="space-y-3 rounded-2xl border border-slate-200/80 p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Rules</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Checked top to bottom on every new conversation — the first match wins.
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={onAdd}>
          <Plus className="h-3.5 w-3.5" /> Add rule
        </Button>
      </div>

      {workflow.rules.length === 0 ? (
        <p className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-500">
          No rules yet — conversations go to the widget&apos;s AI agent or the team queue.
        </p>
      ) : (
        <div className="space-y-2">
          {workflow.rules.map((rule, index) => (
            <div
              key={rule.id}
              className={cn(
                'flex flex-wrap items-center gap-3 rounded-xl border border-slate-100 px-4 py-3',
                !rule.isEnabled && 'bg-slate-50/60',
              )}
            >
              <Checkbox
                label={<span className="sr-only">Enable {rule.name}</span>}
                checked={rule.isEnabled}
                onChange={(e) =>
                  update.mutate(
                    { workflowId: workflow.id, ruleId: rule.id, input: { isEnabled: e.target.checked } },
                    { onError: (err) => toast.error(err.message) },
                  )
                }
              />
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-500">
                {index + 1}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-900">{rule.name}</p>
                <p className="truncate text-xs text-slate-500">
                  When {matchSummary(rule)} → route to {targetName(rule.action)}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Move rule up"
                  disabled={index === 0 || move.isPending}
                  onClick={() =>
                    move.mutate(
                      { workflowId: workflow.id, ruleId: rule.id, input: { direction: 'up' } },
                      { onError: (e) => toast.error(e.message) },
                    )
                  }
                >
                  <ArrowUp className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Move rule down"
                  disabled={index === workflow.rules.length - 1 || move.isPending}
                  onClick={() =>
                    move.mutate(
                      { workflowId: workflow.id, ruleId: rule.id, input: { direction: 'down' } },
                      { onError: (e) => toast.error(e.message) },
                    )
                  }
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEdit(rule)}
                  aria-label={`Edit ${rule.name}`}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant={confirmId === rule.id ? 'danger' : 'ghost'}
                  size="sm"
                  loading={remove.isPending && confirmId === rule.id}
                  aria-label={`Remove ${rule.name}`}
                  onClick={() =>
                    confirmId === rule.id
                      ? remove.mutate(
                          { workflowId: workflow.id, ruleId: rule.id },
                          {
                            onSuccess: () => {
                              toast.success('Rule removed');
                              setConfirmId(null);
                            },
                            onError: (e) => toast.error(e.message),
                          },
                        )
                      : setConfirmId(rule.id)
                  }
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function matchSummary(rule: WorkflowRule): string {
  const match = rule.match;
  const widgetIds = match.widgetIds ?? [];
  const categories = match.categories ?? [];
  const keywords = match.keywords ?? [];
  const parts: string[] = [];
  parts.push(
    widgetIds.length ? `${widgetIds.length} widget${widgetIds.length === 1 ? '' : 's'}` : 'any widget',
  );
  if (categories.length) parts.push(`category: ${categories.join(', ')}`);
  if (keywords.length) parts.push(`keywords: ${keywords.join(', ')}`);
  return parts.join(' · ');
}

type RuleDraft = {
  name: string;
  isEnabled: boolean;
  widgetIds: string[];
  categories: string;
  keywords: string;
  type: WorkflowAction['type'];
  targetId: string;
};

const draftFrom = (rule: WorkflowRule | 'new'): RuleDraft =>
  rule === 'new'
    ? { name: '', isEnabled: true, widgetIds: [], categories: '', keywords: '', type: 'agent', targetId: '' }
    : {
        name: rule.name,
        isEnabled: rule.isEnabled,
        widgetIds: rule.match.widgetIds ?? [],
        categories: (rule.match.categories ?? []).join(', '),
        keywords: (rule.match.keywords ?? []).join(', '),
        type: rule.action.type,
        targetId: rule.action.id,
      };

const splitList = (v: string) =>
  v
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

function RuleModal({
  workflow,
  rule,
  onClose,
}: {
  workflow: Workflow;
  rule: WorkflowRule | 'new';
  onClose: () => void;
}) {
  const toast = useToast();
  const add = useAddWorkflowRule();
  const update = useUpdateWorkflowRule();
  const widgets = useWidgets();
  const agents = useAiAgents();
  const teams = useTeams();
  const users = useAllUsers();
  const [form, setForm] = useState<RuleDraft>(draftFrom(rule));
  const set = <K extends keyof RuleDraft>(k: K, v: RuleDraft[K]) => setForm((f) => ({ ...f, [k]: v }));

  const categorySuggestions = [
    ...new Set((widgets.data ?? []).flatMap((w: Widget) => w.formCategories ?? [])),
  ];

  const targets: { id: string; label: string }[] =
    form.type === 'agent'
      ? (agents.data ?? []).map((a) => ({ id: a.id, label: a.name + (a.isEnabled ? '' : ' (off)') }))
      : form.type === 'team'
        ? (teams.data ?? []).map((t: Team) => ({ id: t.id, label: t.name }))
        : (users.data ?? []).map((u: User) => ({ id: u.id, label: u.name }));

  const payload = (): { ok: true; match: AddRuleInput['match']; action: WorkflowAction } | { ok: false } => {
    if (form.name.trim().length < 2 || !form.targetId) return { ok: false };
    return {
      ok: true,
      match: {
        widgetIds: form.widgetIds,
        categories: splitList(form.categories),
        keywords: splitList(form.keywords),
      },
      action: { type: form.type, id: form.targetId },
    };
  };

  const submit = () => {
    const p = payload();
    if (!p.ok) return;
    const onError = (e: Error) => {
      const fields = e instanceof ApiError ? e.fieldErrors : {};
      toast.error(Object.values(fields)[0] ?? e.message);
    };
    if (rule === 'new') {
      const input: AddRuleInput = {
        name: form.name.trim(),
        isEnabled: form.isEnabled,
        match: p.match,
        action: p.action,
      };
      add.mutate(
        { workflowId: workflow.id, input },
        { onSuccess: () => (toast.success('Rule added'), onClose()), onError },
      );
    } else {
      const input: UpdateRuleInput = {
        name: form.name.trim(),
        isEnabled: form.isEnabled,
        match: p.match,
        action: p.action,
      };
      update.mutate(
        { workflowId: workflow.id, ruleId: rule.id, input },
        { onSuccess: () => (toast.success('Rule saved'), onClose()), onError },
      );
    }
  };

  const pending = add.isPending || update.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      title={rule === 'new' ? 'Add a rule' : `Edit rule — ${rule.name}`}
      description="When an incoming conversation matches the conditions, it is routed to the responder you pick."
      size="lg"
      footer={
        <div className="ml-auto flex gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" loading={pending} onClick={submit} disabled={!payload().ok}>
            {rule === 'new' ? 'Add rule' : 'Save rule'}
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        <Input
          label="Rule name"
          placeholder="Payout questions"
          hint="For your team — visitors never see this."
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
        />

        <div className="rounded-xl border border-slate-200/80 p-4">
          <p className="text-sm font-semibold text-slate-800">When a conversation…</p>
          <p className="mb-3 text-xs text-slate-500">
            Leave a condition empty to match everything. Populated conditions must all match.
          </p>
          <div className="space-y-4">
            <div>
              <p className="mb-2 text-xs font-medium tracking-wide text-slate-500 uppercase">
                Comes from widgets
              </p>
              {(widgets.data ?? []).length === 0 ? (
                <p className="text-sm text-slate-500">No widgets yet.</p>
              ) : (
                <div className="space-y-2">
                  {(widgets.data ?? []).map((w) => (
                    <Checkbox
                      key={w.id}
                      checked={form.widgetIds.includes(w.id)}
                      label={w.name}
                      onChange={(e) =>
                        set(
                          'widgetIds',
                          e.target.checked
                            ? [...form.widgetIds, w.id]
                            : form.widgetIds.filter((id) => id !== w.id),
                        )
                      }
                    />
                  ))}
                </div>
              )}
              <p className="mt-1 text-xs text-slate-400">None selected = every widget.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Categories"
                list="workflow-categories"
                placeholder="Payments & payouts, Bug report"
                hint="Form categories, comma-separated."
                value={form.categories}
                onChange={(e) => set('categories', e.target.value)}
              />
              <datalist id="workflow-categories">
                {categorySuggestions.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
              <Input
                label="Keywords"
                placeholder="payout, refund, invoice"
                hint="Matched anywhere in the subject or message."
                value={form.keywords}
                onChange={(e) => set('keywords', e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200/80 p-4">
          <p className="text-sm font-semibold text-slate-800">Then route it to…</p>
          <p className="mb-3 text-xs text-slate-500">
            An AI agent replies automatically; a team or teammate picks it up in the inbox.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label="Responder"
              value={form.type}
              onChange={(e) => {
                set('type', e.target.value as WorkflowAction['type']);
                set('targetId', '');
              }}
            >
              <option value="agent">AI agent — auto-reply</option>
              <option value="team">Team queue</option>
              <option value="user">Teammate</option>
            </Select>
            <Select label="Route to" value={form.targetId} onChange={(e) => set('targetId', e.target.value)}>
              <option value="">Pick…</option>
              {targets.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <Checkbox
          label="Rule enabled"
          description="Turn off to keep the rule but skip it while routing."
          checked={form.isEnabled}
          onChange={(e) => set('isEnabled', e.target.checked)}
        />
      </div>
    </Modal>
  );
}
